/*
 * @since 2020-11-15 23:17:01
 * @author acrazing <joking.young@gmail.com>
 */

import { Box, hoistMethod, StoreEnhancer } from 'amos';

export const enum DispatchKind {
  Preload = 'P',
  Action = 'A',
  Mutation = 'M',
  Signal = 'S',
  Transaction = 'T',
}

export interface AnyAction {
  type: string;
}

export interface ExternalActionHook {
  subscribe(fn: (action: AnyAction) => void): void;
}

export interface DevToolsOptions {
  /**
   * The name of the store, default is the title of the page
   */
  name?: string;
  /**
   * Force disable devtools even if using {@see forceWithDevTools}
   */
  disable?: boolean;

  externals?: ExternalActionHook[];
}

/**
 * Enable devtools when `process.env.NODE_ENV` is `development`
 * @param options
 */
export function withDevTools(options?: DevToolsOptions): StoreEnhancer {
  if (typeof process === 'object' && process.env.NODE_ENV === 'development') {
    return forceWithDevTools(options);
  }
  return (s) => s;
}

declare const __REDUX_DEVTOOLS_EXTENSION__:
  | undefined
  | {
      connect(options?: {
        name?: string;
      }): {
        init(state: any): void;
        send(action: string | { type: string; [P: string]: any }, state: any): void;
        error(message: string): void;
      };
    };

/**
 * Force enable devtools regardless of `process.env.NODE_ENV`, but you can control it by
 * `options.disable`.
 *
 * @param options
 */
export function forceWithDevTools(options?: DevToolsOptions): StoreEnhancer {
  if (options?.disable) {
    return (s) => s;
  }
  return (store) => {
    if (typeof __REDUX_DEVTOOLS_EXTENSION__ === 'undefined') {
      return store;
    }
    const dev = __REDUX_DEVTOOLS_EXTENSION__.connect(options);
    const { select, dispatch } = store;
    dev.init(store.snapshot());
    options?.externals?.forEach((e) => {
      e.subscribe((action) => dev.send(action, store.snapshot()));
    });
    store.select = hoistMethod(select, (selectable, snapshot) => {
      const shouldSend =
        'initialState' in selectable && !store.snapshot().hasOwnProperty(selectable.key);
      const result = select(selectable, snapshot);
      if (shouldSend) {
        dev.send(`${DispatchKind.Preload}:${(selectable as Box).key}`, store.snapshot());
      }
      return result;
    });
    store.dispatch = hoistMethod(dispatch, (task: any) => {
      const result: any = dispatch(task);
      if (!Array.isArray(task)) {
        const action = normalizeAction(task);
        action && dev.send(action, store.snapshot());
      } else {
        const atoms = task.map(normalizeAction).filter(Boolean);
        dev.send(
          {
            type: `${DispatchKind.Transaction}:[${atoms.map((a) => a!.type).join(',')}]`,
            atoms,
          },
          store.snapshot(),
        );
      }
      return result;
    });
    return store;
  };
}

function normalizeAction(task: any) {
  switch (task.object) {
    case 'mutation':
      return {
        type: `${DispatchKind.Mutation}:${task.box.key}/${task.type || 'anonymous'}`,
        args: task.args,
      };
    case 'signal':
      return {
        type: `${DispatchKind.Signal}:${task.type}`,
        data: task.data,
      };
    case 'action':
      return {
        type: `${DispatchKind.Action}:${task.type || 'anonymous'}`,
        args: task.args,
      };
    default:
      return null;
  }
}
